import { useCallback } from 'react';
import {
  SearchSelectField,
  type SearchSelectOption,
  type SearchSelectPage,
} from '@/components/ui';
import { apiClient } from '@/lib/apiClient';
import type { Artist, ArtistAdminListItem } from '@/lib/api-admin/types';

const PAGE_SIZE = 10;

const toListOption = (a: ArtistAdminListItem): SearchSelectOption => ({
  value: a.artistId,
  label: a.stageName,
  hint:
    [a.type, a.primaryAliasEn ?? undefined, a.vendorId ? `vendor ${a.vendorId}` : undefined]
      .filter(Boolean)
      .join(' · ') || undefined,
});

const toDetailOption = (a: Artist): SearchSelectOption => ({
  value: a.artistId,
  label: a.stageName,
  hint:
    [a.type, a.vendorId ? `vendor ${a.vendorId}` : undefined]
      .filter(Boolean)
      .join(' · ') || undefined,
});

const fetchArtists = async (keyword: string, page: number): Promise<SearchSelectPage> => {
  const trimmed = keyword.trim();
  const query: Record<string, unknown> = {
    size: PAGE_SIZE,
    page,
    status: ['ACTIVE'],
  };
  if (trimmed) query.keyword = trimmed;
  const [data, meta] = await apiClient.request('get', '/admin/v1/artists', {
    params: { query },
  });
  const payload = data as { items: ArtistAdminListItem[] };
  const items = payload.items.map(toListOption);
  const totalItems = meta?.pagination?.totalItems;
  const hasMore =
    totalItems != null
      ? (page + 1) * PAGE_SIZE < totalItems
      : items.length === PAGE_SIZE;
  return totalItems != null
    ? { items, hasMore, total: totalItems }
    : { items, hasMore };
};

const fetchArtist = async (artistId: string): Promise<SearchSelectOption | null> => {
  try {
    const [data] = await apiClient.request('get', '/admin/v1/artists/{artistId}', {
      params: { path: { artistId } },
    });
    return toDetailOption(data as Artist);
  } catch {
    return null;
  }
};

interface ArtistSearchSelectProps {
  value: string | null;
  onChange: (artistId: string | null) => void;
  placeholder?: string;
  disabled?: boolean;
  errorMessage?: string | null;
  testId?: string;
  ariaLabel?: string;
  excludeValues?: ReadonlyArray<string>;
  inputId?: string;
  /** QA-374 — 상품 검색의 아티스트 선택은 카테고리처럼 모달로. */
  mode?: 'inline' | 'modal';
  modalTitle?: string;
}

export const ArtistSearchSelect = ({
  value,
  onChange,
  placeholder = '아티스트명으로 검색',
  disabled,
  errorMessage,
  testId,
  ariaLabel,
  excludeValues,
  inputId,
  mode,
  modalTitle,
}: ArtistSearchSelectProps) => {
  const loadOptions = useCallback(
    (q: string, page: number) => fetchArtists(q, page),
    [],
  );
  const resolveOption = useCallback((v: string) => fetchArtist(v), []);

  return (
    <SearchSelectField
      value={value}
      onChange={onChange}
      loadOptions={loadOptions}
      resolveOption={resolveOption}
      placeholder={placeholder}
      emptyMessage="검색 결과가 없습니다"
      loadingMessage="검색 중..."
      {...(disabled !== undefined ? { disabled } : {})}
      {...(errorMessage !== undefined && errorMessage !== null ? { errorMessage } : {})}
      {...(testId !== undefined ? { testId } : {})}
      {...(ariaLabel !== undefined ? { ariaLabel } : {})}
      {...(inputId !== undefined ? { inputId } : {})}
      {...(excludeValues !== undefined ? { excludeValues } : {})}
      {...(mode !== undefined ? { mode } : {})}
      {...(modalTitle !== undefined ? { modalTitle } : {})}
    />
  );
};
